import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { ActiveSource, SourceTable } from 'types/GlobalSource'

import { useSourcesActions } from 'redux/actions/DataPlatform/connections/Sources'
import { useSourceTableActions } from 'redux/actions/DataPlatform/connections/SourceTables'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useSources } from 'redux/hooks/Sources'
import { useHasSourceTablePreview } from 'redux/hooks/SourceTables'
import { useQuery } from 'utils/hooks'

/** One fixed page — the data section has no paging. */
const PREVIEW_LIMIT = 100

/**
 * Shared setup for the connected data pages: the Homepal gate and the lazy
 * fetch of every source.
 */
export const useSourcesPageData = () => {
  const { isHomepal, loading: accountLoading } = useAccountSettings()
  const [fetched, sources] = useSources()

  const { tryGetAllSources } = useSourcesActions()

  useEffect(() => {
    if (!fetched) {
      tryGetAllSources()
    }
  }, [fetched])

  return { accountLoading, allowed: isHomepal, fetched, sources }
}

/**
 * Which of the source's tables the page is showing. The selection lives in the
 * url so it survives a reload and a section change, and the shell — not the
 * section — is what fetches the preview for it.
 */
export const useActiveSourceTable = (activeSource: ActiveSource | null) => {
  const history = useHistory()
  const { query } = useQuery()
  const { tryGetSourceTablePreview } = useSourceTableActions()

  const tables: SourceTable[] = activeSource?.included.source_tables ?? []
  const requestedId = query.source_table_id
  const activeTable = tables.find((table) => table.id === requestedId) ?? null
  const hasPreview = useHasSourceTablePreview(activeTable?.id ?? null)

  const setActiveTable = (id: string) => {
    history.push({ search: `source_table_id=${id}` })
  }

  // Nothing valid selected but there are tables to show: pick the first one.
  // Replace rather than push, so back doesn't lead to a table-less page.
  useEffect(() => {
    if (!activeTable && tables.length > 0) {
      history.replace({ search: `source_table_id=${tables[0].id}` })
    }
  }, [activeTable, tables.length])

  useEffect(() => {
    if (activeTable && !hasPreview) {
      tryGetSourceTablePreview(activeTable.id, PREVIEW_LIMIT, 0)
    }
  }, [activeTable?.id, hasPreview])

  return { activeTable, setActiveTable, tables }
}
